import { Injectable } from '@angular/core';
import { API } from 'aws-amplify';
import { endpointNames } from 'src/environments/common';
import { errorHandler } from 'src/app/app.utils';
import { ResponseReason } from 'src/app/model';

export interface Lane {
  id: number;
  name?: string;
  zone_id: number;
  position_id: number;
}

@Injectable({
  providedIn: 'root',
})
export class ApiServiceCarCounter {
  private readonly ccApi = endpointNames.carCounter;

  private sleep(ms: number): Promise<void> { return new Promise<void>(resolve => setTimeout(resolve, ms)); }

  async createLane(data: { position_id: number; name?: string }): Promise<{ lane?: Lane; error?: string }> {
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 2000;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      const response = await API.post(this.ccApi, '/lane', { body: data }).catch(errorHandler);

      if (response?.reason === 'ok' || response?.reason === 'created') {
        return { lane: response.data };
      }

      const isPositionNotFound =
        response?.reason === ResponseReason.NotFound ||
        response?.message?.toLowerCase().includes('position not found');
      if (isPositionNotFound && attempt < MAX_RETRIES) {
        await this.sleep(RETRY_DELAY_MS);
        continue;
      }

      if (isPositionNotFound) {
        return { error: 'LANE_POSITION_SYNC_FAILED' };
      }

      return { error: response?.message || 'Error creating lane' };
    }

    return { error: 'Error creating lane' };
  }

  async setDirectionIn(laneId: number, groupId: number): Promise<{ error?: string }> {
    const response = await API.put(this.ccApi, `/lane/${laneId}/directions/in`, { body: { group_id: groupId } }).catch(errorHandler);

    if (response?.reason === 'ok') {
      return {};
    }

    return { error: response?.message || 'Error setting direction in' };
  }

  async setDirectionOut(laneId: number, groupId: number): Promise<{ error?: string }> {
    const response = await API.put(this.ccApi, `/lane/${laneId}/directions/out`, { body: { group_id: groupId } }).catch(errorHandler);

    if (response?.reason === 'ok') {
      return {};
    }

    return { error: response?.message || 'Error setting direction out' };
  }
}
